import {
  Box,
  BoxProps,
  IconButton as ChakraIconButton,
  ComponentWithAs,
  IconButtonProps as _IconButtonProps,
  forwardRef
} from "@chakra-ui/react"
import { IconName } from "@pathwright/pathicons"
import Pathicon from "../pathicon/Pathicon"

type IndicatorProps = Omit<BoxProps, "children">

export function Indicator(boxProps: IndicatorProps) {
  return (
    <Box
      pos="absolute"
      top=".4em"
      right=".4em"
      transform="translate(50%, -50%)"
      w=".6em"
      h=".6em"
      bg="brand.500"
      borderRadius="50%"
      zIndex={1}
      {...boxProps}
    />
  )
}

export type IconButtonProps = Omit<_IconButtonProps, "icon"> & {
  icon: IconName | _IconButtonProps["icon"]
  indicator?: boolean | IndicatorProps
  // For rounding the button, simply supply a width/height size.
  roundSize?: _IconButtonProps["w"]
}

// Main advantage of this component over Chakra's IconButton is that
// it uses our Pathicon component for the icon prop.
const IconButton: ComponentWithAs<"button", IconButtonProps> = forwardRef<
  IconButtonProps,
  "button"
>(({ icon, indicator, roundSize, ...rest }, ref) => {
  const roundSizeProps: Omit<_IconButtonProps, "aria-label"> = roundSize
    ? { h: roundSize, minW: roundSize, isRound: true }
    : {}

  const btn = (
    <ChakraIconButton
      ref={ref}
      // If icon is a string, assume it's a Pathicon name.
      // Otherwise, assume it's a valid React element.
      icon={
        typeof icon === "string" ? (
          <Pathicon icon={icon as IconName} size=".9em" />
        ) : (
          icon
        )
      }
      // Font size of 100% then scaling the Pathicon with ems allows the
      // icon to scale with the button size.
      fontSize="100%"
      {...roundSizeProps}
      {...rest}
    />
  )

  // Conditionally wrap button, grouping with sibling indicator.
  return indicator ? (
    <Box pos="relative" display="inline-block">
      {btn}
      <Indicator {...(typeof indicator === "object" ? indicator : {})} />
    </Box>
  ) : (
    btn
  )
})

export default IconButton
